// ---------------------------------------------------------------------------
// Client networking (ported from ../vyllage/src/net.ts, adapted to 3D).
// Connects over WebSocket, mirrors remote players as full character rigs with
// floating name tags, and renders their motion via entity interpolation: each
// incoming snapshot is timestamped and buffered, and remotes are drawn at a
// fixed delay behind "now" by interpolating between the two snapshots that
// bracket that render time. This replays the server's actual motion smoothly
// instead of chasing the latest sample. The LOCAL player's transform stays
// owned by game.ts; we just relay it with a throttled sendMove().
// ---------------------------------------------------------------------------
import * as THREE from "three";
import { buildPlayer, updatePlayer, setWeapon, setShield, setTool, redressPlayer, startAttack, type AttackSel } from "./player.ts";
import { snapBodyPose } from "./anim/body-pose.ts";
import { makeNameLabel, NAMEPLATE_RANGE } from "./label.ts";
import { occludedByCutaway, hideOutdoorWhileIndoor } from "./cutaway.ts";
import { disposeGroup } from "./meshkit.ts";
import { setWorldItems, addWorldItem, removeWorldItem } from "./items/world.ts";
import { spawnProjectile } from "./projectile.ts";
import { setInventory } from "./inventory.ts";
import { setGear } from "./gear.ts";
import { setWallet } from "./wallet.ts";
import { refreshShopIfOpen } from "./shop.ts";
import { refreshGiftIfOpen } from "./gift.ts";
import { refreshJournalIfOpen } from "./journal.ts";
import { refreshQuestHud } from "./questhud.ts";
import { refreshCoach } from "./coach.ts";
import { itemSpec } from "./items/index.ts";
import { liveById, npcNetHide, npcNetShow, ANIM_RADIUS } from "./npc/index.ts";
import { applyVitals, npcFlinch, npcLunge, npcDefeated, npcRespawn, playNpcAttackSfx, FLINCH_DUR, FLINCH_POP, DEFEAT_DUR } from "./combat.ts";
import { spawnDmgFloat, clearDmgFloats } from "./dmgfloat.ts";
import { spawnCombatFx, spawnAoeFx, clearCombatFx } from "./combatfx.ts";
import { game } from "./state.ts";
import { groundVert, V } from "./sim/vertical.ts";
import { setAirborne, popLanding, applyAirborne } from "./anim/airborne.ts";
import { spawnSplash } from "./splashfx.ts";
import { isWading, navSurfacesNear } from "./physics.ts";
import { WATER_LEVEL } from "./terrain.ts";
import { ACTIVE_MAP_ID } from "./map/active.ts"; // leaf — live binding, re-pointed by activateMap
import { setQuests } from "./quest.ts";
import { setSkills, setInfluence, setStanding } from "./skill.ts";
import { applyPropSync, applyPropDelta, interactFx } from "./interact.ts";
import { applyDoorsSync, applyDoorDelta } from "./doors.ts";
import { applyNibble, applyBite, applyFishOn, applyFishLost, applyCatch, applyEscape, setNets, applyNetDelta, resetFishing, setRemoteBobber, clearRemoteBobber } from "./fishing.ts";
import { reconcileFish, applyFishLeaves, resetFish } from "./fish.ts";
import { sampleBuffer, lerpAngle, type InterpOut } from "./interp.ts";
import { setTraps, applyTrapDelta } from "./hunting.ts";
import { setBrews, applyBrewDelta } from "./brewing.ts";
import { setFires, applyFireDelta, resetCampfires } from "./campfire.ts";
import { applyPanOn, applyCookResult, setRemotePan, clearRemotePan, resetCooking } from "./cooking.ts";
import { onChopOn, onChopResult, resetWoodcutting } from "./woodcutting.ts";
import { onFireOn, onFireResult, resetFirestarting } from "./firestarting.ts";
import { applyWeatherMsg } from "./weather.ts";
import { PLAYER_BAR_Y, type PlayerBar } from "./playerbars.ts";
import { DEFAULT_APPEARANCE, WEAR_SLOTS, WS_CLOSE_DISPLACED, type Appearance, type Gear, type WearSlot, type NetPlayer, type NetNpc, type NetPlayerWire, type NetNpcWire, type NetFish, type NetQuest, type NetSkill, type NetStanding, type NetDeploy, type TrapDeploy, type BrewDeploy, type FireDeploy, type WorldItem, type ItemType, type StateLeave, type ImStats, type ClientMsg, type ServerMsg } from "./shared.ts";

export const me = { id: "", name: "", look: { ...DEFAULT_APPEARANCE } as Appearance };

// One timestamped server snapshot of a remote (client receive time, in ms).
type Sample = { t: number; x: number; y: number; z: number; ry: number; moving: boolean; running: boolean; air: boolean };

type Remote = {
  rig: THREE.Group;
  color: number; // shirt color, for the minimap dot
  name: string; // retained from the ENTER fragment (board N3) — slim per-tick updates omit it
  look: Appearance; // retained from the ENTER fragment — immutable in-session, reused for gear re-dress
  buf: Sample[]; // chronological snapshot buffer (oldest first)
  weapon: ItemType | null; // currently rendered in-hand weapon (for change detection)
  offhand: ItemType | null; // currently rendered offhand shield (plan 14, for change detection)
  tool: ItemType | null; // currently rendered held tool (rod/hatchet), for change detection
  gear: Gear; // currently rendered worn-gear record (for change detection)
  label: THREE.Sprite; // the name-tag sprite (rebuilt when the title changes)
  title: string | null; // currently rendered floating title (for change detection)
  hp: number; // last seen HP, to detect a drop → flinch
  maxHp: number; // for the overhead HP bar fill
  down: boolean; // last seen knockout state, to detect the topple/revive edges
  fighting: boolean; // engaged with a fightable NPC → show the overhead HP bar
  snare: boolean; // crowd-control flags (wire present-only)
  stun: boolean;
  slow: boolean;
  chopSeq: number; // last seen chop swing seq → fire hatchet attack on bump
  fireSeq: number; // last seen spark seq → fire strike anim on bump
  flinchT: number; // remaining hit-reaction pop (seconds)
  downT: number; // topple progress timer (seconds since collapse began)
  // rendered (interpolated) transform — what's actually drawn this frame
  x: number; y: number; z: number; ry: number; moving: boolean; running: boolean;
  air: boolean; // latest airborne flag (gravity plan) — drives the airborne pose
  lastY: number; // previous frame's rendered y — the vy estimate for the lean
};

// How far behind real time we render remotes. Near-ring entities arrive at 50Hz
// (~20ms), so 80ms is ~4 samples of headroom — almost always a sample on both sides
// of the render time to interpolate between, even with jitter. Mid-ring remotes
// (10Hz / 100ms) will intermittently outrun this and hit the extrapolation path below
// (bounded by MAX_EXTRAP).
const INTERP_DELAY = 80; // ms
// When the buffer runs dry (no snapshot newer than the render time), keep the
// remote moving along its last known velocity for up to this long before giving
// up and holding. Bridges short jitter/packet-loss gaps without big overshoot.
const MAX_EXTRAP = 120; // ms

const others = new Map<string, Remote>();

// Per-NPC interpolation buffer + change-detection latches. NPC *state* is owned
// by the server; we buffer its transform for smooth interpolation and latch the
// discrete fields (hp / atkSeq / defeated) to fire reaction animations once each.
type NpcSample = { t: number; x: number; y: number; z: number; ry: number };
type NpcRemote = { buf: NpcSample[]; hp: number; atkSeq: number; defeated: boolean };
const npcRemotes = new Map<string, NpcRemote>();

let scene: THREE.Scene;
let ws: WebSocket | undefined;
let onChat: (name: string, text: string) => void = () => {};

/** The character state the server hands us on join (and on every reconnect): the
 *  SAVED transform + vitals + equipped weapon + inventory to hydrate locally.
 *  `map` names the map doc this character is on — game.ts builds THAT world. */
export type SpawnInfo = {
  map: string;
  x: number; y: number; z: number; ry: number;
  name: string; look: Appearance;
  hp: number; maxHp: number;
  /** Attack-gate pool after skill-box bonuses (optional for older servers). */
  stamina?: number;
  maxStamina?: number;
  weapon: ItemType | null;
  offhand: ItemType | null;
  tool: ItemType | null;
  gear: Gear;
  inventory: { slot: number; type: ItemType; count: number }[];
  obol: number;
  /** First-login cue (plan 26): play this cinematic before revealing the world.
   *  net.ts arms the message hold itself when this is set — game.ts releases it
   *  via finishTransfer() after the post-cinematic world build (or immediately,
   *  on the unknown-id fallback). */
  cinematic?: string;
};

/** An in-session map transfer (plan 24): the arrival pose rides the message so
 *  game.ts can swap worlds + reposition without a reload. */
export type TransferInfo = { map: string; x: number; y: number; z: number; ry: number };

let onSpawn: (s: SpawnInfo) => void = () => {};
let onTransfer: (t: TransferInfo) => void = () => {};
let onNeedCharacter: () => void = () => {};
let onAuthFail: () => void = () => {};
let onDisplaced: () => void = () => {};

// ---- The transfer hold-and-replay queue (plan 24) ---------------------------
// Between the `transfer` message and the finished world swap, every inbound
// message is HELD, then replayed onto the freshly built world. Never drop them:
// world items are enter-once on the wire (the interest contract's leave lists
// are the only removal), so a dropped `state` enter is an item lost until it
// re-enters interest — players/NPCs would self-heal at ring cadence, items not.
let transferring = false;
const heldMsgs: MessageEvent[] = [];

/** game.ts calls this once the new world is built + the player repositioned:
 *  replay everything that arrived mid-swap (state enters, props/nets/traps/
 *  brews resyncs) in arrival order. A nested `transfer` inside the replay
 *  re-enters the hold correctly (it re-sets the flag and re-queues the tail). */
export function finishTransfer(): void {
  transferring = false;
  for (const e of heldMsgs.splice(0)) {
    if (transferring) { heldMsgs.push(e); continue; }
    handleMessage(e);
  }
}

/** Arm the hold from OUTSIDE a transfer — a talk-triggered cinematic (plan 26)
 *  swaps the live world for a stage and back, and every message that lands
 *  meanwhile must replay onto the rebuilt world (same enter-once rule as a
 *  portal transfer). Release is the same finishTransfer(). */
export function armHold(): void {
  transferring = true;
}
let sessionToken = "";
let sessionCharacterId = ""; // which saved hero this session is playing (resent on reconnect)
let reconnectDelay = 0;  // ms; grows on repeated failures
let socketOpenedAt = 0;  // DIAGNOSTIC: ms timestamp the current socket opened, for close-duration logging
let authOk = false;      // did the CURRENT socket authenticate (receive `welcome`)?
let authFailCount = 0;   // consecutive sockets that closed without ever authenticating

// Conversion tracking (site plan phase 1): the marketing site's Play buttons
// land here with ?ref=site. Read it ONCE at boot, scrub it from the URL (so
// bookmarks/shares don't re-report), and ride it on the FIRST WS connect only
// (reconnects aren't new arrivals) — the server logs it at session start.
let pendingRef: string | null = null;
{
  const q = new URLSearchParams(location.search);
  const r = q.get("ref");
  if (r) {
    pendingRef = r.slice(0, 32);
    q.delete("ref");
    const rest = q.toString();
    history.replaceState(null, "", location.pathname + (rest ? `?${rest}` : "") + location.hash);
  }
}

/** Connect with a session token and the chosen character. `characterId` names
 *  WHICH saved hero to enter as (the select screen picked it); it's resent on
 *  every reconnect so a drop never silently loads a different character. */
export function connect(
  s: THREE.Scene,
  opts: { token: string; characterId: string },
  chatHandler: (n: string, t: string) => void,
  handlers: { onSpawn: (s: SpawnInfo) => void; onTransfer?: (t: TransferInfo) => void; onNeedCharacter?: () => void; onAuthFail?: () => void; onDisplaced?: () => void },
) {
  scene = s;
  onChat = chatHandler;
  onSpawn = handlers.onSpawn;
  onTransfer = handlers.onTransfer ?? (() => { location.reload(); });
  onNeedCharacter = handlers.onNeedCharacter ?? (() => {});
  onAuthFail = handlers.onAuthFail ?? (() => {});
  onDisplaced = handlers.onDisplaced ?? (() => {});
  sessionToken = opts.token;
  sessionCharacterId = opts.characterId;
  openSocket();
}

function openSocket() {
  authOk = false;
  const proto = location.protocol === "https:" ? "wss" : "ws";
  const refArg = pendingRef ? `&ref=${encodeURIComponent(pendingRef)}` : "";
  pendingRef = null;
  ws = new WebSocket(`${proto}://${location.host}/ws?token=${encodeURIComponent(sessionToken)}${refArg}`);
  ws.onopen = () => {
    reconnectDelay = 0;
    socketOpenedAt = Date.now(); // DIAGNOSTIC: for the close-duration log below
    // Always name the chosen character; the server loads it (ownership-checked)
    // and, on a bad/empty id, replies needCharacter → back to the select screen.
    send({ t: "join", characterId: sessionCharacterId });
  };
  ws.onmessage = handleMessage;
  ws.onclose = (ev) => {
    // DIAGNOSTIC (temporary): capture the close code/reason + how long the socket
    // lived. code 1006 = abnormal/no close frame (raw network drop or edge reset);
    // a clean code with a reason = the server closed us on purpose (backpressure /
    // displacement). A consistent heldMs points at a fixed-interval killer.
    const heldMs = socketOpenedAt ? Date.now() - socketOpenedAt : -1;
    console.warn(`[disconnect] code=${ev.code} reason=${ev.reason || "-"} wasClean=${ev.wasClean} heldMs=${heldMs} authOk=${authOk}`);
    // The socket's interest state died with it: the server's fresh connection
    // knows nothing about what this one delivered, so drop every mirrored
    // entity — the reconnect streams the world back in as fresh enters. A
    // mid-transfer disconnect also drops the held queue (the re-join re-sends
    // everything), else the hold flag would wedge the fresh socket's messages.
    transferring = false;
    heldMsgs.length = 0;
    resetMirrors();
    // A live cast died with the socket server-side (its close handler ends it);
    // drop ours too — a reconnected cast would wait on a bobber the new
    // connection's server knows nothing about.
    resetFishing();
    // Same for a live cook (its server-side session was cancelled on close):
    // drop cook mode + every remote pan, else a mid-cook disconnect leaves the
    // player locked in the 'select' phase (which has no AFK timeout) until they
    // manually Esc. Campfire mirrors re-sync via `fires` on the re-join.
    resetCooking();
    resetWoodcutting();
    resetFirestarting();
    resetCampfires();
    // Deliberate eviction: this character was opened in another tab/window (or on
    // another device), and the server closed THIS session in favour of the newer
    // one. Do NOT auto-reconnect — that would re-displace the new session and the
    // two would ping-pong forever. Sit still and let the player decide (Play here
    // takes the session back via resumeHere).
    if (ev.code === WS_CLOSE_DISPLACED) { onDisplaced(); return; }
    // A socket that closes without ever authenticating is either a rejected
    // upgrade (bad/expired token) OR a transient outage that looks identical at
    // the WS API. Retry a few times so a server blip self-heals; only after
    // repeated auth-less closes do we give up and bounce to sign-in.
    if (!authOk) {
      if (++authFailCount >= 4) { onAuthFail(); return; }
    }
    onChat("system", `disconnected (${ev.code}) — reconnecting…`);
    reconnectDelay = Math.min(reconnectDelay ? reconnectDelay * 2 : 500, 8000);
    setTimeout(openSocket, reconnectDelay);
  };
}

/** Take the session back into THIS tab after being displaced (the "Play here"
 *  button). Reconnecting re-joins, which displaces whatever tab currently holds
 *  the character — a deliberate, one-click hand-off rather than an auto-war. */
export function resumeHere() {
  authFailCount = 0;
  reconnectDelay = 0;
  openSocket();
}

function handleMessage(e: MessageEvent) {
    // Mid-transfer: hold everything for finishTransfer's replay (see the queue
    // note above — dropping would lose enter-once world items).
    if (transferring) { heldMsgs.push(e); return; }
    // Guard the whole parse+dispatch: a malformed/partial frame (or a throw deep
    // in reconciliation) must not abort the rest of THIS message — the `leave`
    // lists are the only removal path and are never re-sent, so a throw before
    // applyLeaves would permanently leak whatever left interest this tick. Log +
    // drop the one frame; the socket + interpolation keep running.
    let m: ServerMsg;
    try { m = JSON.parse(e.data) as ServerMsg; }
    catch (err) { console.error("[net] bad server frame — dropped", err); return; }
    try {
    if (m.t === "welcome") {
      authOk = true;
      authFailCount = 0;
      me.id = m.id;
      serverWeatherDev = !!m.dev; // the F4 panel greys out when the server won't take overrides
      serverDevCommands = !!(m as { devCommands?: boolean }).devCommands;
    } else if (m.t === "spawn") {
      // Our chosen character, loaded from durable storage.
      // The imstats opt-in lives on the (now-dead) old connection server-side, so
      // re-subscribe on every fresh socket while F3 is still on — else the debug HUD
      // reads "awaiting imstats…" forever after a reconnect.
      if (imStatsOptIn) send({ t: "imstats", on: true });
      me.name = m.name;
      me.look = m.look as Appearance;
      // Reconnect onto a DIFFERENT map than the built world (a transfer's
      // message was lost to the disconnect): arm the hold HERE, exactly like
      // the `transfer` branch, so everything after this spawn replays onto the
      // new world — game.ts's swapWorld (run from onSpawn) releases it via
      // finishTransfer(). Armed net-side so no caller ordering can miss it.
      // A first-login cinematic cue (plan 26) arms the SAME hold: everything
      // after this spawn replays onto the real world once the cinematic's stage
      // is torn down and the spawn map is built.
      if ((game.joined && m.map !== ACTIVE_MAP_ID) || (!game.joined && m.cinematic)) transferring = true;
      onSpawn({
        map: m.map,
        x: m.x, y: m.y, z: m.z, ry: m.ry,
        name: m.name, look: m.look as Appearance,
        hp: m.hp, maxHp: m.maxHp,
        stamina: m.stamina as number | undefined,
        maxStamina: m.maxStamina as number | undefined,
        weapon: (m.weapon ?? null) as ItemType | null,
        offhand: (m.offhand ?? null) as ItemType | null,
        tool: (m.tool ?? null) as ItemType | null,
        gear: (m.gear ?? {}) as Gear,
        inventory: (m.inventory ?? []) as SpawnInfo["inventory"],
        obol: (m.obol as number) ?? 0,
        cinematic: m.cinematic as string | undefined,
      });
    } else if (m.t === "needCharacter") {
      onNeedCharacter();
    } else if (m.t === "state") {
      // Interest-managed (see IM_DEFAULTS in shared.ts for the contract):
      // records here are entities in OUR area of interest that are due an
      // update this tick — an absent entity is NOT gone (mid/far rings update
      // less often); removal is only ever the explicit `leave` lists below.
      lastStateBytes = (e.data as string).length;
      stateBytesAccum += lastStateBytes;
      reconcile(m.players as NetPlayerWire[], m.ts as number | undefined);
      if (m.npcs) reconcileNpcs(m.npcs as NetNpcWire[], m.ts as number | undefined);
      // World items entering our interest area (drops, loot, or just walking
      // toward them). Idempotent adds — items/world.ts despawns any duplicate.
      if (m.items) for (const it of m.items as WorldItem[]) addWorldItem(it);
      // Water-population fish shadows (Living Waters) — the 4th mirrored kind.
      if (m.fish) reconcileFish(m.fish as NetFish[], m.ts as number | undefined);
      if (m.leave) applyLeaves(m.leave as StateLeave);
      // World clock is server-owned (shared day/night); adopt it each snapshot.
      if (typeof m.tod === "number") game.tod = m.tod;
    } else if (m.t === "imstats") {
      imStats = m as ImStats;
    } else if (m.t === "vitals") {
      // Authoritative HP + knockout (server-owned as of Step 3). Mirror it onto the HUD;
      // combat.ts presents the flinch / topple / revive. We no longer compute our own HP.
      // `stag` (plan 14): a blocked hit emptied the pool — combat.ts presents the stagger.
      // `stamina`/`maxStamina` carry skill-box pool bonuses (Wind etc.).
      applyVitals(
        m.hp as number, m.maxHp as number, !!m.down,
        m.stag as number | undefined,
        m.snare as number | undefined,
        m.stun as number | undefined,
        m.slow as number | undefined,
        m.slowMul as number | undefined,
        m.stamina as number | undefined,
        m.maxStamina as number | undefined,
      );
    } else if (m.t === "pose") {
      // Authoritative body pose (owner-only echo — our own record never rides
      // the snapshot): sit snaps, emote confirms, damage-breaks, and any-action
      // stands all land through this one path.
      const sp = (m.id as string | null) ?? null;
      if (sp !== game.pose) {
        game.pose = sp;
        if (game.player) game.player.userData.bodyPose = sp;
      }
    } else if (m.t === "correction") {
      // The server rejected our position (a clamped teleport / speed-hack, or a big desync
      // after a lag spike). Snap the local player to the authoritative spot + kill any arc.
      // While snared/stunned/knocked the server owns the transform mid-impulse — follow
      // without groundVert (which would cancel a loft).
      if (game.player) {
        game.player.position.x = m.x as number;
        game.player.position.z = m.z as number;
        game.feetY = m.y as number;
        if (game.snareT > 0 || game.stunT > 0 || game.kbLocal) {
          game.vert.y = m.y as number;
          if (m.y as number > game.vert.peakY) game.vert.peakY = m.y as number;
        } else {
          groundVert(game.vert, m.y as number);
          game.kbLocal = false;
        }
        // A seat snap owns the facing too (sit → face outward from the bench).
        if (typeof m.ry === "number") game.player.rotation.y = m.ry;
      }
    } else if (m.t === "transfer") {
      // Our character moved to another map (plan 24): swap worlds in-session.
      // Set the hold flag SYNCHRONOUSLY so nothing the server sent after
      // `transfer` (same-tick state, props/nets/traps/brews resyncs) is
      // processed against the OLD world — game.ts's swapWorld tears down,
      // rebuilds, repositions, then finishTransfer() replays the queue.
      transferring = true;
      onTransfer({ map: m.map, x: m.x, y: m.y, z: m.z, ry: m.ry });
    } else if (m.t === "swing") {
      // A remote player threw an attack — replay the swing on their rig (skips us:
      // we're not in `others`, and we already predicted our own swing locally).
      const r = others.get(m.id as string);
      if (r) startAttack(r.rig, m.style as AttackSel);
    } else if (m.t === "combatFx") {
      // Onset CC VFX (snare / stun / knockback). World-anchored burst.
      spawnCombatFx(m.kind, m.x, m.y, m.z);
      // Local player: seed timers / kb follow so input locks match before vitals.
      if (m.target === "player" && m.id === me.id) {
        if (m.kind === "snare" && game.snareT <= 0) game.snareT = 0.05; // vitals refreshes true remaining
        if (m.kind === "stun" && game.stunT <= 0) game.stunT = 0.05;
        // Seed until vitals arrives with true remaining + slowMul.
        if (m.kind === "slow" && game.slowT <= 0) game.slowT = 0.05;
        if (m.kind === "knockback") {
          // Server owns XZ (and loft y via corrections). Do NOT seed client
          // air/vy — stepVertical is skipped while kbLocal so we don't fight.
          game.kbLocal = true;
          game.kbT = 0.5; // ≈ KB_TIME + margin; refreshed if another bash lands
        }
      }
    } else if (m.t === "aoe") {
      // Special-move volume telegraph (sector cone/circle or pierce line).
      if (m.kind === "sector") {
        spawnAoeFx(m.x, m.y, m.z, { kind: "sector", arcDeg: m.arcDeg, range: m.range, ry: m.ry });
      } else {
        spawnAoeFx(m.x, m.y, m.z, { kind: "line", length: m.length, width: m.width, ry: m.ry });
      }
    } else if (m.t === "projectile") {
      // A fired arrow (server-authoritative). Everyone including the shooter flies
      // the same cosmetic arrow; a miss arrives with its endpoint already nudged
      // wide and spawns a "Miss" float at `at*` on arrival. Hits land damage/flinch
      // server-side on arrival (pending-hits → HP drop → red dmg float).
      spawnProjectile({
        fromX: m.fromX, fromY: m.fromY, fromZ: m.fromZ,
        toX: m.toX, toY: m.toY, toZ: m.toZ,
        speed: m.speed,
        hit: m.hit,
        atX: m.atX, atY: m.atY, atZ: m.atZ,
      });
    } else if (m.t === "chat") {
      onChat(m.name, m.text);
    } else if (m.t === "inventory") {
      // Authoritative bag snapshot (server-owned as of Step 2). Mirror it onto the HUD.
      setInventory(m.bag as { slot: number; type: ItemType; count: number }[]);
      refreshShopIfOpen(); // buy/sell affordability + "have ×N" depend on the bag
      refreshGiftIfOpen(); // gift modal bag mirror + auto-close on successful Take it
      refreshCoach(); // bag may now hold the coached equip item
    } else if (m.t === "gear") {
      // Authoritative worn-gear record (owner-only, like the bag). Mirrors onto
      // the paperdoll HUD + re-dresses the local rig (gear.ts).
      setGear(m.gear as Gear);
    } else if (m.t === "wallet") {
      // Authoritative obol balance (owner-only, like vitals). Mirror it + restate the
      // shop panel's prices/affordability if it's open.
      setWallet(m.obol as number);
      refreshShopIfOpen();
    } else if (m.t === "quests") {
      // Personal quest progress (sent only to us). Drives quest dialogue + the
      // journal + the top-center tracker / world waypoint marker.
      setQuests(m.quests as NetQuest[], (m.kills as number) ?? 0);
      refreshJournalIfOpen();
      refreshQuestHud();
      refreshCoach();
    } else if (m.t === "skills") {
      // Personal skill XP + unlocked boxes (owner-only, like quests). Mirror it +
      // toast any XP gain.
      setSkills(m.skills as NetSkill[]);
      refreshJournalIfOpen(); // XP bars / box eligibility depend on the rows
    } else if (m.t === "influence") {
      // Global skill-point pool (owner-only). Mirror it for the Skills book spend UI.
      setInfluence(m.influence as number);
      refreshJournalIfOpen(); // spend budget changed → restate box eligibility
    } else if (m.t === "standing") {
      // Per-track rank/progress + equipped title (owner-only). Mirror it.
      setStanding(m.standing as NetStanding[], (m.title as string | null) ?? null);
      refreshJournalIfOpen(); // rank pips/titles + rank-gated boxes depend on it
    } else if (m.t === "props") {
      // Full interactable-prop state (opened chests + hidden flowers + cut trees),
      // sent on join/reconnect — a full reconcile, like the inventory mirror.
      applyPropSync((m.opened ?? []) as string[], (m.hidden ?? []) as string[], (m.cut ?? []) as string[]);
    } else if (m.t === "prop") {
      // One prop changed (broadcast): opened / hidden / shown / cut.
      applyPropDelta(m.id as string, m.state as string);
    } else if (m.t === "doors") {
      // Full open-door list on join/transfer (default closed; sparse open ids).
      applyDoorsSync((m.open ?? []) as string[]);
    } else if (m.t === "door") {
      // One structure door toggled (map broadcast).
      applyDoorDelta(m.id as string, !!m.open);
    } else if (m.t === "interactFx") {
      // Personal feedback line for our own interaction (toast only).
      interactFx(m.note as string | undefined);
    } else if (m.t === "weather") {
      // Global weather changed (or the join baseline landed): ease toward the
      // new target. Change-driven — NOT part of the `state` snapshot.
      applyWeatherMsg(m);
    } else if (m.t === "nibble") {
      // Fishing (Living Waters, owner-only): a nibble tug — bobber dip, no hook.
      applyNibble();
    } else if (m.t === "bite") {
      // The bite landed — fishing.ts arms the grab-window sweep + plunges the bobber.
      applyBite();
    } else if (m.t === "fishOn") {
      // Hooked! Expand the server-seeded pattern + mount the reel minigame.
      applyFishOn(m.seed as number, m.fish as ItemType);
    } else if (m.t === "fishLost") {
      // The fish left (spooked / bored) — the cast stays out, back to waiting.
      applyFishLost(m.why as "spooked" | "interest");
    } else if (m.t === "catch") {
      applyCatch((m.items ?? []) as ItemType[], m.kind as "angling" | "netting");
    } else if (m.t === "escape") {
      applyEscape(m.reason as string | undefined);
    } else if (m.t === "nets") {
      // Full deployed-net sync on join/reconnect (personalized `mine` flags).
      setNets((m.nets ?? []) as NetDeploy[]);
    } else if (m.t === "net") {
      // One net deployed/collected (broadcast, personalized).
      applyNetDelta(m.op as "add" | "remove", m.net as NetDeploy | undefined, m.id as string | undefined);
    } else if (m.t === "traps") {
      // Full deployed-trap sync on join/reconnect (plan 11 — the nets pattern).
      setTraps((m.traps ?? []) as TrapDeploy[]);
    } else if (m.t === "trap") {
      // One trap deployed/collected (broadcast, personalized).
      applyTrapDelta(m.op as "add" | "remove", m.trap as TrapDeploy | undefined, m.id as string | undefined);
    } else if (m.t === "brews") {
      // Full deployed-fermenter sync on join/reconnect (plan 18 CP3 — the nets pattern).
      setBrews((m.brews ?? []) as BrewDeploy[]);
    } else if (m.t === "brew") {
      // One fermenter deployed/collected (broadcast, personalized).
      applyBrewDelta(m.op as "add" | "remove", m.brew as BrewDeploy | undefined, m.id as string | undefined);
    } else if (m.t === "fires") {
      // Full burning-campfire sync on join/reconnect/transfer (plan 25).
      setFires((m.fires ?? []) as FireDeploy[]);
    } else if (m.t === "fire") {
      // One campfire lit / burned out (broadcast, personalized).
      applyFireDelta(m.op as "add" | "remove", m.fire as FireDeploy | undefined, m.id as string | undefined);
    } else if (m.t === "panOn") {
      // Cooking (plan 25, owner-only): the server accepted the cook — cooking.ts
      // expands the seed into the prompt pattern and mounts the pan minigame.
      applyPanOn(m.seed as number, m.type as ItemType);
    } else if (m.t === "cookResult") {
      applyCookResult(m.outcome as "cooked" | "burnt" | "invalid", m.item as ItemType | undefined);
    } else if (m.t === "chopOn") {
      onChopOn(m.id as string, m.seed as number);
    } else if (m.t === "chopResult") {
      onChopResult(
        m.outcome as "cut" | "failed" | "invalid" | "exhausted",
        m.logs as ItemType | undefined,
        m.count as number | undefined,
        m.sta as number | undefined,
      );
    } else if (m.t === "fireOn") {
      onFireOn(m.seed as number, m.x as number, m.z as number);
    } else if (m.t === "fireResult") {
      onFireResult(m.outcome as "lit" | "failed" | "invalid");
    } else {
      // Unknown message type (a newer server during a rolling deploy, say) —
      // ignore it, but leave a dev breadcrumb rather than failing silently.
      console.debug?.("[net] ignoring unknown server message", (m as { t?: unknown }).t);
    }
    } catch (err) {
      console.error("[net] handler threw for", (m as { t?: unknown }).t, err);
    }
}

// ---- Interest-management client state ---------------------------------------

// Debug/HUD instrumentation: last `state` payload size + a rolling bytes/sec,
// and the latest server `imstats` payload (null until we opt in via F3).
let lastStateBytes = 0;
let stateBytesAccum = 0;
let bytesWindowAt = 0;
let stateBytesPerSec = 0;
let imStats: ImStats | null = null;

/** Client-side network stats for the debug HUD (src/imdebug.ts). */
export function netStats(): { others: number; stateBytes: number; stateBytesPerSec: number; im: ImStats | null } {
  const now = performance.now();
  if (now - bytesWindowAt >= 1000) {
    stateBytesPerSec = Math.round(stateBytesAccum / Math.max(0.001, (now - bytesWindowAt) / 1000));
    stateBytesAccum = 0;
    bytesWindowAt = now;
  }
  return { others: others.size, stateBytes: lastStateBytes, stateBytesPerSec, im: imStats };
}

let imStatsOptIn = false; // survives reconnects; the server-side flag dies with the socket

/** Opt in/out of the server's ~2Hz `imstats` debug feed. */
export function sendImstats(on: boolean) {
  imStatsOptIn = on;
  if (!on) imStats = null;
  send({ t: "imstats", on });
}

/** Tear down one mirrored remote player (disconnect or left our interest area). */
function removePlayer(id: string): void {
  const r = others.get(id);
  if (!r) return;
  scene.remove(r.rig);
  clearRemoteBobber(id); // drop their bobber float if a line was out when they left
  clearRemotePan(id);    // ...and their pan if they were mid-cook (plan 25)
  // Free the rig's GPU resources — body prims + any held weapon/shield/tool + worn
  // parts. Every OTHER teardown path in the codebase disposeGroups (npc/index.ts,
  // chardesign, charselect, cinematic/stage); skipping it here leaked per-build
  // geometry on every remote that left interest (and, at ~50Hz interest churn in a
  // busy area, over a session that's a real WebGL memory climb). disposeGroup keeps
  // the shared UNIT_BOX/SOFT_BOX + cached toon materials (only `userData.owned`
  // materials dispose), so it's safe on a rig built from the shared factories.
  disposeGroup(r.rig);
  // The name label is a per-remote canvas-backed sprite (makeNameLabel) — free
  // it like the title-swap branch does; resetMirrors runs on every map swap
  // now, so skipping this leaks a texture+material per remote per crossing.
  r.label.material.map?.dispose();
  r.label.material.dispose();
  others.delete(id);
}

/** An NPC left our interest area: hide its Live record (invisible + non-solid;
 *  focus/talk release via the visibility guards) and drop its interp buffer so a
 *  later re-enter seeds fresh instead of lerping across the map. */
function npcLeave(id: string): void {
  const L = liveById(id);
  if (L) npcNetHide(L);
  npcRemotes.delete(id);
}

/** Apply the `state` message's explicit removals (the only removal path). */
function applyLeaves(leave: StateLeave): void {
  if (leave.players) for (const id of leave.players) removePlayer(id);
  if (leave.npcs) for (const id of leave.npcs) npcLeave(id);
  if (leave.items) for (const id of leave.items) removeWorldItem(id);
  if (leave.fish) applyFishLeaves(leave.fish);
}

/** Drop every mirrored entity (remote rigs, dynamic NPCs, world items). Called
 *  on socket close: a fresh connection's interest state starts empty server-side,
 *  so everything re-enters from scratch and nothing stale lingers here. Also
 *  the first teardown step of an in-session map swap (game.ts swapWorld) — the
 *  old map's remotes/NPC mirrors/items are exactly as dead there. */
export function resetMirrors(): void {
  if (!scene) return; // never connected
  for (const id of [...others.keys()]) removePlayer(id);
  for (const id of [...npcRemotes.keys()]) npcLeave(id);
  resetFish();
  setWorldItems([]);
  clearDmgFloats(); // drop mid-flight numbers with the world
  clearCombatFx();
  game.snareT = 0; game.stunT = 0; game.kbLocal = false; game.kbT = 0;
}

// Maps the server clock onto our performance.now() timeline so we can play
// remotes back on the server's true emission cadence. clockOffset ≈
// performance.now() - serverTs at receive (network latency + epoch skew). We
// snap it to the smallest value seen (the least-delayed packet = best estimate)
// and let it drift up slowly to track clock drift. `haveClock` stays false if
// the server never sends `ts` (old server during a rolling deploy), in which
// case we fall back to arrival-time stamping.
let clockOffset = 0;
let haveClock = false;

function reconcile(list: NetPlayerWire[], serverTs?: number) {
  const arrival = performance.now();
  const hasTs = typeof serverTs === "number" && Number.isFinite(serverTs);
  if (hasTs) {
    const off = arrival - (serverTs as number);
    if (!haveClock) { clockOffset = off; haveClock = true; }
    else if (off < clockOffset) clockOffset = off;        // lower latency: snap
    else clockOffset += (off - clockOffset) * 0.01;       // else drift up slowly
  }
  // Timeline for buffered samples: server clock when available, else arrival.
  // NOTE (interest management): a player absent from this payload is NOT gone —
  // mid/far-ring entities update at a reduced rate. Removal happens ONLY via
  // the state message's explicit `leave.players` list (applyLeaves).
  const stamp = hasTs ? (serverTs as number) : arrival;
  for (const p of list) {
    if (p.id === me.id) {
      // Defensive only — the interest engine never includes our own record.
      // Our POSE confirms through the owner-only `pose` message instead.
      continue; // we own ourselves
    }
    const sample: Sample = { t: stamp, x: p.x, y: p.y, z: p.z, ry: p.ry, moving: p.moving, running: p.running, air: !!p.air };
    const cur = others.get(p.id);
    if (cur) {
      cur.buf.push(sample);
      const w = p.weapon ?? null;
      // Mirror worn-gear changes onto the remote's body: a changed record
      // rebuilds the rig in place (same group — label/buffers untouched) with
      // the NEW weapon seated, so a same-tick equip doesn't rebuild the hand
      // twice (the diff below then no-ops). Plain per-slot compare — this runs
      // per snapshot for every near remote, so no stringify allocation here.
      const gear = p.gear ?? {};
      if (WEAR_SLOTS.some((s) => gear[s] !== cur.gear[s])) {
        // Re-dress with the RETAINED look (slim updates omit it; look is
        // immutable in-session, so cur.look from the enter is authoritative).
        redressPlayer(cur.rig, cur.look, gear, w ? itemSpec(w) : null);
        cur.gear = gear;
        cur.weapon = w;
      }
      // Mirror equip changes onto the remote's hand.
      if (w !== cur.weapon) {
        setWeapon(cur.rig, w ? itemSpec(w) : null);
        cur.weapon = w;
      }
      // Offhand shield + the hold-to-block pose (plan 14).
      const oh = p.offhand ?? null;
      if (oh !== cur.offhand) {
        setShield(cur.rig, oh ? itemSpec(oh) : null);
        cur.offhand = oh;
      }
      // Held fishing rod (plan 10) — main-hand exclusive with the weapon, so
      // diff it AFTER the weapon (an equip swap this tick seats the newer one).
      const tl = p.tool ?? null;
      if (tl !== cur.tool) {
        setTool(cur.rig, tl ? itemSpec(tl) : null);
        cur.tool = tl;
      }
      cur.rig.userData.blocking = !!p.blocking;
      cur.rig.userData.fishing = !!p.fishing; // a line out → the two-handed rod pose (plan 10)
      cur.rig.userData.chopping = !!p.chopping;
      cur.rig.userData.fireStarting = !!p.fireStarting;
      // Firestarting owns kneel for the whole session — key off fireStarting so
      // a mid-spark breakPose/stand on the wire can't leave remotes standing.
      cur.rig.userData.bodyPose = p.fireStarting ? "kneeling" : (p.pose ?? null);
      // Hatchet ready stance while cutting; swing on chopSeq bump.
      if (p.chopping) cur.rig.userData.combatReady = true;
      const cseq = p.chopSeq ?? 0;
      if (cseq !== cur.chopSeq) {
        if (cseq > cur.chopSeq) startAttack(cur.rig, "primary");
        cur.chopSeq = cseq;
      }
      // Spark strikes while firestarting (kneel from fireStarting above).
      const fseq = p.fireSeq ?? 0;
      if (fseq !== cur.fireSeq) {
        if (fseq > cur.fireSeq) startAttack(cur.rig, "primary");
        cur.fireSeq = fseq;
      }
      // ...and the bobber float where their line landed (cx/cz ride the snapshot).
      if (p.fishing && typeof p.cx === "number" && typeof p.cz === "number") setRemoteBobber(p.id, p.cx, p.cz);
      else clearRemoteBobber(p.id);
      // ...and the pan + sizzling ingredient at their hearth (plan 25 — same mirror).
      if (p.cooking && typeof p.hx === "number" && typeof p.hz === "number") setRemotePan(p.id, p.hx, p.hz, p.cooking);
      else clearRemotePan(p.id);
      // Title change (earned/equipped a new rank title): the name tag is a baked
      // canvas sprite, so rebuild it — remove + dispose the old one, add a fresh one.
      const title = p.title ?? null;
      if (title !== cur.title) {
        cur.rig.remove(cur.label);
        cur.label.material.map?.dispose();
        cur.label.material.dispose();
        const label = makeNameLabel(cur.name, title ?? undefined); // retained name (slim updates omit it)
        label.position.set(0, 3.0, 0); // float just above the head so it doesn't cover the face
        cur.rig.add(label);
        cur.label = label;
        cur.title = title;
      }
      // Combat-ready stance rides the snapshot; updatePlayer reads it off userData.
      // (Chopping already forced ready above when mid-cut.)
      if (!p.chopping) cur.rig.userData.combatReady = !!p.ready;
      // HP drop → flinch pop; knockout edge → start the topple (revive resets it).
      if (p.hp < cur.hp) cur.flinchT = FLINCH_DUR;
      cur.hp = p.hp;
      cur.maxHp = p.maxHp;
      cur.fighting = !!p.fighting;
      cur.snare = !!p.snare;
      cur.stun = !!p.stun;
      cur.slow = !!p.slow;
      if (p.down && !cur.down) cur.downT = 0;   // collapsed: begin the fall
      else if (!p.down && cur.down) {           // revived: snap upright
        cur.downT = 0;
        cur.rig.rotation.z = 0;
      }
      cur.down = p.down;
    } else {
      // New player: build a rig in their chosen look + worn gear + a name tag,
      // snap into place. Identity (name/look) rides the ENTER fragment only; if
      // a slim update somehow arrives before the enter (should never — the
      // interest engine force-includes the full enter first, and a dropped
      // enter frame re-arms via restoreStateEnters), skip it and wait for the
      // enter rather than build a rig with an undefined look. Loudly — a
      // recurring warn here means the enter-before-update invariant broke.
      if (p.look === undefined || p.name === undefined) {
        console.warn(`[net] slim update for unbuilt player ${p.id} — waiting for its enter`);
        continue;
      }
      const rig = buildPlayer(p.look, p.gear ?? {});
      rig.position.set(p.x, p.y, p.z);
      rig.rotation.y = p.ry;
      const label = makeNameLabel(p.name, p.title ?? undefined);
      label.position.set(0, 3.0, 0); // float just above the head so it doesn't cover the face
      rig.add(label);
      const w = p.weapon ?? null;
      if (w) setWeapon(rig, itemSpec(w));
      const oh = p.offhand ?? null;
      if (oh) setShield(rig, itemSpec(oh));
      const tl = p.tool ?? null;
      if (tl) setTool(rig, itemSpec(tl));
      rig.userData.blocking = !!p.blocking;
      rig.userData.fishing = !!p.fishing;
      rig.userData.chopping = !!p.chopping;
      rig.userData.fireStarting = !!p.fireStarting;
      rig.userData.bodyPose = p.fireStarting ? "kneeling" : (p.pose ?? null);
      rig.userData.combatReady = !!(p.ready || p.chopping);
      if (p.fishing && typeof p.cx === "number" && typeof p.cz === "number") setRemoteBobber(p.id, p.cx, p.cz);
      if (p.cooking && typeof p.hx === "number" && typeof p.hz === "number") setRemotePan(p.id, p.hx, p.hz, p.cooking);
      scene.add(rig);
      others.set(p.id, {
        rig,
        color: p.look.shirt, // minimap dot tint
        name: p.name, look: p.look, // retained identity (enter-only fields)
        buf: [sample],
        weapon: w,
        offhand: oh,
        tool: tl,
        gear: p.gear ?? {},
        label,
        title: p.title ?? null,
        hp: p.hp, maxHp: p.maxHp, down: !!p.down, fighting: !!p.fighting,
        snare: !!p.snare, stun: !!p.stun, slow: !!p.slow,
        chopSeq: p.chopSeq ?? 0, fireSeq: p.fireSeq ?? 0, flinchT: 0, downT: 0,
        x: p.x, y: p.y, z: p.z, ry: p.ry, moving: p.moving, running: p.running,
        air: !!p.air, lastY: p.y,
      });
    }
  }
}

// Fold a server NPC snapshot onto the local Live records: buffer the transform
// for interpolation, push the latest combat numbers straight through, and fire
// one-shot reaction animations on the edges (a hit, a punch, a defeat/respawn).
const _snapPose = { dy: 0, pitch: 0, roll: 0 };

function reconcileNpcs(list: NetNpcWire[], serverTs?: number): void {
  const hasTs = typeof serverTs === "number" && Number.isFinite(serverTs);
  const stamp = hasTs ? (serverTs as number) : performance.now();
  for (const n of list) {
    const L = liveById(n.id);
    if (!L) continue;
    // ENTER: a record for a hidden NPC means it just came into our interest
    // area. Drop any stale interp buffer/latches (same discipline as the
    // respawn path below — no lerp across the map, no spurious flinch/defeat
    // edges), show it in the right pose, and snap it to the wire transform.
    if (L.netHidden) {
      npcRemotes.delete(n.id);
      npcNetShow(L, n.defeated);
      L.group.position.set(n.x, n.y, n.z);
      L.group.rotation.y = n.ry;
      L.feetY = n.y;
      // Land in the authored pose at full weight — a seated innkeeper mustn't
      // sit down every time she enters our interest area.
      if (L.rig) snapBodyPose(L.group, L.rig, n.pose ?? null, _snapPose);
      if (n.defeated) L.group.position.y = n.y + 0.3; // corpse rests on the grass (combat.ts topple lift)
      else if (L.collider) { L.collider.x = n.x; L.collider.z = n.z; }
    }
    let nr = npcRemotes.get(n.id);
    if (!nr) {
      nr = { buf: [], hp: n.hp, atkSeq: n.atkSeq, defeated: n.defeated };
      npcRemotes.set(n.id, nr);
    }
    // Only buffer transforms while alive — a corpse's transform is frozen and
    // owned by the topple animation, so buffering it would just leak samples.
    if (!n.defeated) nr.buf.push({ t: stamp, x: n.x, y: n.y, z: n.z, ry: n.ry });

    // Combat numbers (drive the overhead bars, projected by combat.ts).
    // Maxes ride the ENTER fragment only (slim updates omit them — they're
    // static post-spawn); keep the retained values between enters.
    if (n.maxHp !== undefined) L.maxHp = n.maxHp;
    if (n.maxStamina !== undefined) L.maxStamina = n.maxStamina;
    L.stamina = n.stamina;
    L.engaged = n.engaged;
    L.fleeing = n.fleeing; // wildlife scamper flag → drives the frantic hop in updateNpcs
    L.air = !!n.air; // ballistic fall (gravity plan) → the airborne pose in updateNpcs
    L.snare = !!n.snare;
    L.slow = !!n.slow;
    L.stun = !!n.stun;
    L.group.userData.bodyPose = n.pose ?? null; // authored idle pose → the blended pose layer (driveGait)
    // Who (if anyone) is mid-conversation with this NPC — talk.ts reads it to
    // confirm our own talk request and to show the slot as busy for others.
    L.talker = n.talker;
    L.talkingToMe = n.talker === me.id; // our own cutscene → npc/index hides its bubble + name tag
    // Who (if any player) it's currently ATTACKING (plan 14 auto-engage) —
    // combat.ts pops the engaged presentation when this is us, no click needed.
    L.tgt = n.tgt ?? null;

    if (n.hp < nr.hp) {
      // Took damage → flinch pop + red floating number (authoritative amount).
      npcFlinch(L);
      const dmg = nr.hp - n.hp;
      const p = L.group.position;
      spawnDmgFloat(p.x, L.feetY + L.height + 0.45, p.z, dmg);
    }
    L.hp = n.hp; nr.hp = n.hp;
    if (n.atkSeq !== nr.atkSeq) {                 // threw a punch / loosed a shot
      // Jointed humanoids (goblin, guard, archer, …) play the real attack clip
      // via driveGait → startRigAttack("primary") → applyWeaponPose (unarmed jab
      // or the held weapon's swing/draw). The whole-body haymaker lean is the
      // LEGACY rigless reaction — only fire it when there's no humanoid rig, so
      // a goblin punch reads as a punch, not a headbutt lean.
      if (!L.rig) npcLunge(L);
      L.group.userData.studioAttack = true;       // limb / clip attack (gait or pose)
      playNpcAttackSfx(L);                      // wolf snarl etc. (sporadic per kind)
      nr.atkSeq = n.atkSeq;
    }

    if (n.defeated && !nr.defeated) npcDefeated(L);        // dropped → topple
    else if (!n.defeated && nr.defeated) {                 // respawned → reset + snap
      npcRespawn(L);
      nr.buf.length = 0; // drop stale death-spot samples so we don't lerp across the map
    }
    nr.defeated = n.defeated;
    L.defeated = n.defeated;
  }
}

/** Per-frame: interpolate each server NPC onto its Live record (same delayed
 *  bracket scheme as remote players). Defeated NPCs are left to combat.ts (it
 *  owns the topple), so we freeze their transform here. Call before updateNpcs. */
const NPC_INTERP: InterpOut = { x: 0, y: 0, z: 0, ry: 0 }; // reused scratch (single-threaded, consumed each iter)
export function updateNetNpcs(): void {
  const renderT = sampleRenderT();
  for (const [id, nr] of npcRemotes) {
    if (nr.defeated) continue; // corpse transform is owned by the topple animation
    const L = liveById(id);
    if (!L) continue;
    if (!sampleBuffer(nr.buf, renderT, NPC_INTERP)) continue; // empty buffer → freeze
    const { x, y, z, ry } = NPC_INTERP;
    L.group.position.set(x, y, z);
    L.group.rotation.y = ry;
    L.feetY = y; // y already carries the small walk bob; close enough for bar/ring anchoring
    // Keep the local collider under the networked goblin so the player still
    // bumps into it where it actually is on every screen — and RE-BASE its
    // vertical band when the NPC changed floors (gravity plan: it was stamped
    // once at spawn; a storey move left it blocking the wrong level).
    if (L.collider) {
      L.collider.x = x; L.collider.z = z;
      if (L.collider.minY !== undefined && Math.abs(L.feetY - L.collider.minY) > 0.3) {
        const h = (L.collider.maxY ?? L.collider.minY + 2) - L.collider.minY;
        L.collider.minY = L.feetY;
        L.collider.maxY = L.feetY + h;
      }
    }
  }
}

/** The interpolation render time on the sample clock — game.ts feeds it to
 *  fish.ts's updateFish (the fish mirror lives outside this module but renders
 *  on the same delayed timeline as NPCs/remotes). */
export function sampleRenderT(): number {
  return (haveClock ? performance.now() - clockOffset : performance.now()) - INTERP_DELAY;
}


// Entity interpolation: render each remote at (now - INTERP_DELAY) by finding
// the two buffered snapshots that bracket that render time and lerping between
// them, then drive its walk cycle + ground height. Call every render frame.
export function updateRemotes(camera: THREE.Camera, dt: number, time: number) {
  // Render time, expressed in the same clock the samples are stamped in: the
  // estimated server clock when synced, else our local arrival clock.
  const nowOnSampleClock = haveClock ? performance.now() - clockOffset : performance.now();
  const renderT = nowOnSampleClock - INTERP_DELAY;
  for (const r of others.values()) {
    const buf = r.buf;

    // Prune: while the 2nd sample is still at/older than renderT, the 1st is no
    // longer needed as the lower bracket. Leaves buf[0] as the newest sample
    // with t <= renderT, so memory stays bounded to the interpolation window.
    while (buf.length >= 2 && buf[1].t <= renderT) buf.shift();

    if (buf.length === 0) continue;

    if (renderT <= buf[0].t) {
      // Not enough history yet (just spawned / big stall): hold at the oldest.
      r.x = buf[0].x; r.y = buf[0].y; r.z = buf[0].z; r.ry = buf[0].ry; r.moving = buf[0].moving; r.running = buf[0].running;
      r.air = buf[0].air;
    } else {
      // Find the bracket [a, b] with a.t <= renderT <= b.t.
      let a = buf[0], b = buf[buf.length - 1];
      for (let i = 0; i < buf.length - 1; i++) {
        if (buf[i].t <= renderT && renderT <= buf[i + 1].t) {
          a = buf[i]; b = buf[i + 1];
          break;
        }
      }
      if (renderT >= b.t) {
        // Ran past the newest sample (packet loss / no fresh data). Extrapolate
        // along the last segment's velocity for a bounded window so the remote
        // keeps gliding instead of freezing, then hold once the window expires.
        const prev = buf.length >= 2 ? buf[buf.length - 2] : b;
        const seg = b.t - prev.t;
        const ahead = Math.min(renderT - b.t, MAX_EXTRAP);
        if (seg > 0 && b.moving) {
          const vx = (b.x - prev.x) / seg, vz = (b.z - prev.z) / seg;
          r.x = b.x + vx * ahead;
          r.z = b.z + vz * ahead;
        } else {
          r.x = b.x; r.z = b.z;
        }
        if (b.air && seg > 0) {
          // Mid-flight: continue the BALLISTIC arc (derived vy + gravity) so a
          // jumping/falling remote keeps arcing through packet loss instead of
          // hanging in the air. Grounded remotes keep the hold-the-floor rule.
          const vy = (b.y - prev.y) / seg; // units per ms
          const aheadS = ahead / 1000; // sample clock is ms; gravity is u/s²
          r.y = b.y + vy * ahead - 0.5 * V.gravity * aheadS * aheadS;
        } else {
          r.y = b.y; // don't extrapolate height — hold the last known floor
        }
        r.ry = b.ry;
        r.moving = b.moving;
        r.running = b.running;
        r.air = b.air;
      } else {
        const span = b.t - a.t;
        const f = span > 0 ? (renderT - a.t) / span : 0;
        r.x = a.x + (b.x - a.x) * f;
        r.y = a.y + (b.y - a.y) * f;
        r.z = a.z + (b.z - a.z) * f;
        r.ry = lerpAngle(a.ry, b.ry, f);
        r.moving = a.moving || b.moving;
        r.running = a.running || b.running;
        r.air = a.air || b.air;
      }
    }

    // Animation distance gate (the updateNpcs rule): a remote past ANIM_RADIUS
    // of the CAMERA is beyond the far plane + fog — provably off-screen — so
    // skip the full gait/weapon-pose/body-pose pass and just carry the
    // networked transform. Measured from the camera, NOT the player: the
    // radius is camera-visibility-derived (CAM_FAR + 10), and a zoomed-out
    // camera (F7) can sit ~60u from the player — a player-relative gate would
    // freeze a remote that's plainly on-screen. userData.poseRoot keeps its
    // last offset, so a seated far remote stays seated. Resumes on re-approach.
    const cdx = r.x - camera.position.x, cdz = r.z - camera.position.z;
    const animate = cdx * cdx + cdz * cdz < ANIM_RADIUS * ANIM_RADIUS;
    // Airborne presentation (gravity plan): vy estimated from the rendered y,
    // the lean applied after the gait pass, a splash/dip on the landing edge.
    const vyEst = dt > 0 ? (r.y - r.lastY) / dt : 0;
    const wasAir = r.rig.userData.wasAir === true;
    if (wasAir && !r.air) {
      // Deck over wet terrain = dry landing (no splash on the fishing pier).
      if (isWading(r.x, r.z, r.y, navSurfacesNear(r.x, r.z))) spawnSplash(r.x, WATER_LEVEL, r.z);
      else popLanding(r.rig, vyEst < -12); // fast arrival = a hard landing
    }
    r.rig.userData.wasAir = r.air;
    r.lastY = r.y;
    setAirborne(r.rig, r.air, vyEst);
    const bob = animate ? updatePlayer(r.rig, dt, r.moving && !r.air, time, r.running && !r.air) : 0;
    // Height comes straight from the wire (the owning client's resolved feet
    // height), so remotes stand on the same floors/stairs they actually walked.
    r.rig.position.set(r.x, r.y + bob, r.z);
    r.rig.rotation.y = r.ry;
    // Body pose root (seated/prone/…) — updatePlayer ran the blended pose pass
    // and reported the offset on userData.poseRoot (zeroed while standing).
    // Written before the KO topple below so a downed remote still wins.
    const poseRoot = r.rig.userData.poseRoot as { dy: number; pitch: number; roll: number } | undefined;
    if (poseRoot) {
      r.rig.position.y += poseRoot.dy;
      r.rig.rotation.x = poseRoot.pitch;
      r.rig.rotation.z = poseRoot.roll;
    }
    // Airborne lean + landing dip (gravity plan) — after the gait/pose writes.
    applyAirborne(r.rig, dt);

    // Combat reactions (same curves combat.ts uses locally): a hit-flinch scale
    // pop, and the knockout topple onto the side. Applied LAST so they win over
    // the gait pose updatePlayer just set.
    if (r.flinchT > 0) {
      r.flinchT = Math.max(0, r.flinchT - dt);
      r.rig.scale.setScalar(1 + FLINCH_POP * (r.flinchT / FLINCH_DUR));
    } else if (r.rig.scale.x !== 1) {
      r.rig.scale.setScalar(1); // guarded — skip the every-frame reset write
    }
    if (r.down) {
      r.downT = Math.min(DEFEAT_DUR, r.downT + dt);
      const k = r.downT / DEFEAT_DUR;
      r.rig.rotation.z = k * (Math.PI / 2);
      r.rig.position.y = r.y + k * 0.3; // rest on the ground, not half-buried
    }

    if (animate) {
      // Hide remotes standing on a cut-away upper level so they don't float,
      // and outdoor remotes while the local player is indoors (blackout).
      // hideOutdoorWhileIndoor is 1-frame stale here (updateRemotes runs before
      // cutaway) — same as occludedByCutaway; the ease makes it unnoticeable.
      r.rig.visible =
        !occludedByCutaway(r.x, r.y, r.z) && !hideOutdoorWhileIndoor(r.x, r.z);

      // Name tag: only within reach of the local PLAYER (same rule as NPCs), so
      // distant remotes don't litter the view. Child of the rig, so an invisible
      // rig (cutaway) hides it for free; this is the near-vs-far cut.
      const pdx = r.x - game.player.position.x, pdz = r.z - game.player.position.z;
      r.label.visible =
        r.rig.visible &&
        pdx * pdx + pdz * pdz <= NAMEPLATE_RANGE * NAMEPLATE_RANGE;
    } else {
      // Off-screen: the cutaway/label checks only matter near the player
      // (NAMEPLATE_RANGE 16, shelter volumes are player-local — both << the
      // gate), so skip them; guarded writes pin the far state. Still honor
      // indoor outdoor-hide so a far remote doesn't flash visible on re-approach.
      const show = !hideOutdoorWhileIndoor(r.x, r.z);
      if (r.rig.visible !== show) r.rig.visible = show;
      if (r.label.visible) r.label.visible = false;
    }
  }
}

/** Remote players currently in combat (and standing), with the world anchor point
 *  for their overhead HP bar. Built from the rendered transform, so call after
 *  updateRemotes. The local player is excluded (it reads the bottom vitals HUD). */
export function combatants(): PlayerBar[] {
  const out: PlayerBar[] = [];
  for (const [id, r] of others) {
    if (!r.fighting || r.down) continue;
    out.push({
      id, wx: r.x, wy: r.y + PLAYER_BAR_Y, wz: r.z, hp: r.hp, maxHp: r.maxHp,
      snare: r.snare, stun: r.stun, slow: r.slow,
    });
  }
  return out;
}

/** Smoothed map positions + shirt colors of every remote player, for the minimap. */
export function remoteDots(): { x: number; z: number; color: number }[] {
  const out: { x: number; z: number; color: number }[] = [];
  for (const r of others.values()) out.push({ x: r.x, z: r.z, color: r.color });
  return out;
}

function send(obj: ClientMsg) {
  if (ws?.readyState === 1) ws.send(JSON.stringify(obj));
}

let lastSent = 0;
/** Throttled (~50/sec) relay of the local player's transform. */
export function sendMove(x: number, y: number, z: number, ry: number, moving: boolean, running: boolean) {
  const now = performance.now();
  if (now - lastSent < 20) return;
  lastSent = now;
  send({ t: "move", x, y, z, ry, moving, running });
}

export function sendChat(text: string) {
  send({ t: "chat", text });
}

/** Emote into a named body pose (null = stand up). Server-validated (rejected
 *  while another activity owns the body); the owner-only `pose` echo is the
 *  confirm — game.pose adopts from it. */
export function sendPose(id: string | null) {
  send({ t: "pose", id });
}

/** Sit on a furniture seat (click-to-sit). The server validates range +
 *  occupancy, snaps us onto the seat (a `correction`), and applies the
 *  seatable pose — confirmed like any pose, via the owner-only `pose` echo. */
export function sendSit(id: string) {
  send({ t: "sit", id });
}

/** Ack a finished/skipped cinematic (plan 26): the server marks it seen on the
 *  character (skip counts — a hard-close mid-film replays next login, which is
 *  the self-healing default). */
export function sendCinematicDone(id: string) {
  send({ t: "cinematicDone", id });
}

/** Ask the server to pick up a world item (it confirms via `leave.items` on the
 *  state message + an `inventory` echo to us). */
export function sendPickup(id: string) {
  send({ t: "pickup", id });
}

/** Tell the server we dropped an item at (x,y,z); nearby players (us included)
 *  see it enter via `state.items` on their next interest refresh. */
export function sendDrop(type: ItemType, x: number, y: number, z: number) {
  send({ t: "drop", type, x, y, z });
}

/** Tell the server which weapon we have equipped (or null when unequipped). It
 *  rides the state snapshot so every client renders it in our hand. Pass
 *  `off: true` for the OFFHAND slot (shields, plan 14). */
export function sendEquip(type: ItemType | null, off = false) {
  send({ t: "equip", type, ...(off ? { off } : {}) });
}

/** Raise/lower the equipped shield (hold-to-block, plan 14). Server-validated;
 *  the pose rides the snapshot as `blocking` for remotes + the NPC AI. */
export function sendBlock(on: boolean) {
  send({ t: "block", on });
}

/** Equip (or clear) a fishing rod on the held-tool channel (plan 10). Server-
 *  validated (owned + a rod + its box gate); rides the snapshot as `tool`. */
export function sendEquipTool(type: ItemType | null) {
  send({ t: "equip", type, tool: true });
}

/** Fishing intents (Living Waters). The server owns the encounter + every
 *  roll: `cast` lands the bobber at a validated water point, `reel` is THE
 *  PULL (hooks only inside the authoritative bite window), `reelInput` streams
 *  one minigame arrow's outcome (server re-judges the tension), `castEnd`
 *  cancels. Results come back owner-only as
 *  `nibble`/`bite`/`fishOn`/`fishLost`/`catch`/`escape`. */
export function sendCast(x: number, z: number) {
  send({ t: "cast", x, z });
}
export function sendReel() {
  send({ t: "reel" });
}
export function sendReelInput(i: number, hit: boolean) {
  send({ t: "reelInput", i, hit });
}
export function sendCastEnd() {
  send({ t: "castEnd" });
}

/** Tree-cutting intents: start session / stream minigame input / cancel. */
export function sendChopStart(id: string) {
  send({ t: "chopStart", id });
}
export function sendChopInput(i: number, hit: boolean) {
  send({ t: "chopInput", i, hit });
}
export function sendChopEnd() {
  send({ t: "chopEnd" });
}
/** Answer the catch-preview HUD: `keep` = the item TYPES to bank (the rest are
 *  thrown back). Empty = throw everything back. */
export function sendKeepCatch(keep: ItemType[]) {
  send({ t: "keepCatch", keep });
}

/** Cooking intents (plan 25, active branch): the server gates the hearth, rolls
 *  the pattern seed, and judges every prompt. `cook` commits a carried raw
 *  ingredient, `panInput` streams one prompt's timing verdict (the reelInput
 *  twin — the server re-derives the hit from arrival time), `cookEnd` cancels
 *  (Esc; refund only while untouched). Results come back owner-only as
 *  `panOn`/`cookResult`. */
export function sendCook(type: ItemType) {
  send({ t: "cook", type });
}
export function sendPanInput(i: number, hit: boolean) {
  send({ t: "panInput", i, hit });
}
export function sendCookEnd() {
  send({ t: "cookEnd" });
}
/** Firestarting intents: begin spark session at (x,z) / stream input / cancel.
 *  Materials are spent only on success (server). */
export function sendFireStart(x: number, z: number) {
  send({ t: "fireStart", x, z });
}
export function sendFireInput(i: number, hit: boolean) {
  send({ t: "fireInput", i, hit });
}
export function sendFireEnd() {
  send({ t: "fireEnd" });
}
/** Put out your own campfire early (server validates ownership + range). */
export function sendExtinguishFire(id: string) {
  send({ t: "extinguishFire", id });
}
/** Eat one unit of a food item (plan 18). The HP heal is server-authoritative (it
 *  comes back as `vitals`); the stamina restore is predicted client-side. */
export function sendConsume(type: ItemType) {
  send({ t: "consume", type });
}

/** Netting intents (plan 10): anchor a carried net at a water point / haul one
 *  of our ready nets. The server owns the soak timer (durable, absolute) and
 *  the bulk roll; deltas come back as `net`/`nets` + an `inventory` echo. */
export function sendDeployNet(type: ItemType, x: number, z: number) {
  send({ t: "deployNet", type, x, z });
}
export function sendCollectNet(id: string) {
  send({ t: "collectNet", id });
}

/** Trapping intents (plan 11): anchor a carried trap on dry ground / haul one
 *  of our ready traps. The server owns the soak timer (durable, absolute) and
 *  the haul rolls; deltas come back as `trap`/`traps` + an `inventory` echo. */
export function sendDeployTrap(type: ItemType, x: number, z: number) {
  send({ t: "deployTrap", type, x, z });
}
export function sendCollectTrap(id: string) {
  send({ t: "collectTrap", id });
}

/** Brewing intents (plan 18 CP3): anchor a carried fermenter on dry ground / haul
 *  one of our ready ones. The server owns the durable soak timer + the haul rolls;
 *  deltas come back as `brew`/`brews` + an `inventory` echo. */
export function sendDeployBrew(type: ItemType, x: number, z: number) {
  send({ t: "deployBrew", type, x, z });
}
export function sendCollectBrew(id: string) {
  send({ t: "collectBrew", id });
}

/** Wear a carried wearable in its body slot (or take the slot's piece off with
 *  type=null). The server moves it bag ↔ gear and echoes both mirrors; the
 *  record rides the state snapshot so every client dresses our rig. */
export function sendWear(slot: WearSlot, type: ItemType | null) {
  send({ t: "wear", slot, type });
}

/** Drop a quantity of obol as a coin pile. The server validates we hold that many,
 *  debits the wallet, and spawns the pile at our feet (it owns the drop point). */
export function sendDropObol(amount: number) {
  send({ t: "dropObol", amount });
}

/** Shop intents — the server owns the wallet + bag and validates against the
 *  shopkeeper's conversation lock. `buy` grants one ware for its obol price; `sell`
 *  buys one back. Both echo an updated `wallet` + `inventory`. */
export function sendBuy(shop: string, type: ItemType) {
  send({ t: "buy", shop, type });
}
export function sendSell(shop: string, type: ItemType) {
  send({ t: "sell", shop, type });
}

/** Accept a free dialogue gift (`giveItem` → gift modal Take it). Server
 *  validates the talk lock + that the partner offers this type. */
export function sendAcceptGift(type: ItemType) {
  send({ t: "acceptGift", type });
}

/** Combat intents — the server is authoritative for NPCs. `engage`/`disengage`
 *  track which NPC we're focused on (drives its AI + the shared HP bars);
 *  `attack` asks the server to apply a punch (it validates reach + damage). */
export function sendEngage(id: string) {
  send({ t: "engage", id });
}
export function sendDisengage(id: string) {
  send({ t: "disengage", id });
}
/** A melee swing — resolved server-side as a frontal-arc cleave (plan 14).
 *  `id` = the primary-target hint (the focused NPC, so a deliberate attack can
 *  land on a non-hostile); `move` = a special move for slots 2-3. */
export function sendAttack(id?: string, move?: string) {
  send({ t: "attack", id, move });
}

/** Fire a ranged shot at a focused NPC. The server rolls the hit by distance band,
 *  broadcasts the projectile, and applies damage on arrival. Ammo is spent
 *  client-side before this is sent (see combat.ts). */
export function sendFire(id: string) {
  send({ t: "fire", id });
}

/** Instant ranged special (e.g. slow-arrow). */
export function sendFireMove(id: string, move: string) {
  send({ t: "fireMove", id, move });
}

/** Long-shot charge begin / free cancel / full-charge release. */
export function sendChargeBegin(move: string) {
  send({ t: "chargeBegin", move });
}
export function sendChargeCancel() {
  send({ t: "chargeCancel" });
}
export function sendChargeRelease(id: string) {
  send({ t: "chargeRelease", id });
}

/** Interact with a focused prop (open a chest / shake a tree / pick a flower).
 *  The server validates range + state, rolls any drop chance, and answers with
 *  a `prop` delta broadcast (+ an `interactFx` toast to us). See interact.ts. */
export function sendInteract(id: string) {
  send({ t: "interact", id });
}

export function sendDoor(id: string) {
  send({ t: "door", id });
}

/** Talk intents — the server coordinates conversations (exclusive per NPC).
 *  `talk` requests the lock (granted only if free + in range; confirmed via the
 *  snapshot's `talker`); `talkEnd` releases it. */
export function sendTalk(id: string) {
  send({ t: "talk", id });
}
export function sendTalkEnd(id: string) {
  send({ t: "talkEnd", id });
}

/** Quest intents — the server owns quest progress. `questStart` asks to take a quest
 *  (via a dialogue choice); `questAdvance` turns in a dialogue-driven step. The server
 *  validates both and replies with an updated personal `quests` message. */
export function sendQuestStart(id: string) {
  send({ t: "questStart", id });
}
export function sendQuestAdvance(id: string) {
  send({ t: "questAdvance", id });
}

/** Dev-only quest scrubber (journal debug tools). Server drops unless
 *  DEV_COMMANDS=1 (`welcome.devCommands`). `to` = step id | "restart" |
 *  "complete" | "start". */
export function sendQuestDev(id: string, to: string) {
  send({ t: "questDev", id, to });
}
/** Spend influence to unlock a skill box (the Skills book spend button). The server
 *  validates every gate + echoes the authoritative skills/influence — optimistic UI
 *  should reconcile from that echo, never assume success. */
export function sendUnlockBox(skill: string, box: string) {
  send({ t: "unlockBox", skill, box });
}

/** Relay an attack swing so other clients replay it on our rig (one-shot event). */
export function sendSwing(style: AttackSel) {
  send({ t: "swing", style });
}

/** Toggle our combat-ready stance for remotes (sticky — rides the snapshot). */
export function sendReady(on: boolean) {
  send({ t: "ready", on });
}

/** Toggle our in-combat flag for remotes (drives the overhead HP bar). Sticky —
 *  rides the snapshot, edge-triggered from combat.ts on engage/disengage. */
export function sendFighting(on: boolean) {
  send({ t: "fighting", on });
}

/** Hold/release the shared world clock's fast-forward (T). The server owns `tod`
 *  and advances it while any player is fast-forwarding, so the day/night is shared. */
export function sendTodFF(on: boolean) {
  send({ t: "tod", on });
}

// ---- Weather dev override (the F4 panel) ------------------------------------

/** Did the server boot with WEATHER_DEV=1 (rides `welcome`)? When false the
 *  server silently drops `weatherDev`, so the panel shows a disabled notice. */
export let serverWeatherDev = false;

/** Did the server boot with DEV_COMMANDS=1 (rides `welcome.devCommands`)?
 *  Gates the journal quest scrubber + chat `/map` + F8 grant panel etc. */
export let serverDevCommands = false;

/** Fire a dev weather override. No ack — the echoed `weather` broadcast (to
 *  every client; weather is one global state) is the confirmation. */
export function sendWeatherDev(msg: Omit<Extract<ClientMsg, { t: "weatherDev" }>, "t">) {
  send({ t: "weatherDev", ...msg });
}

/** Dev bag grant (F8 panel / `/give`). Server drops unless DEV_COMMANDS=1;
 *  success echoes as inventory + a system chat line. */
export function sendDevGrant(type: string, count?: number) {
  send({ t: "devGrant", type, count });
}
